import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { of } from 'rxjs';
import { catchError, finalize, switchMap, take } from 'rxjs/operators';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { AuthService } from '../../auth/auth.service';
import { SessionStorageService } from '../../helpers/session-storage.service';
import { Customer } from '../customer.model';
import { CustomerService } from '../customer.service';

@Component({
  selector: 'app-customer-view',
  standalone: true,
  templateUrl: './customer-view.component.html',
  styleUrls: ['../../shared/management-pages.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterLink,
    AvatarModule,
    ButtonModule,
    TagModule
  ]
})
export class CustomerViewComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly customerService = inject(CustomerService);
  private readonly authService = inject(AuthService);
  private readonly sessionStorage = inject(SessionStorageService);

  customer = signal<Customer | null>(null);
  isProfile = signal(false);
  isLoading = signal(false);
  error = signal<string | null>(null);

  ngOnInit(): void {
    this.loadCustomer();
  }

  loadCustomer(): void {
    const id = this.route.snapshot.paramMap.get('id');
    this.isProfile.set(!id);
    this.isLoading.set(true);
    this.error.set(null);

    const request$ = id
      ? this.customerService.getCustomerById(Number(id))
      : this.authService.userData.pipe(
          take(1),
          switchMap(user => {
            const sessionCustomer = this.sessionStorage.getCustomer();
            const firebaseId = user?.uid ?? sessionCustomer?.firebaseId;
            return firebaseId ? this.customerService.getCustomerByFirebaseId(firebaseId) : of(null);
          })
        );

    request$.pipe(
      finalize(() => this.isLoading.set(false)),
      catchError(error => {
        this.error.set(error?.message || 'Unable to load customer.');
        return of(null);
      })
    ).subscribe(response => {
      this.customer.set(response?.data ?? null);
      if (!response?.data) {
        this.error.set('Customer profile was not found.');
      }
    });
  }

  roleNames(customer: Customer): string[] {
    return (customer.roles ?? []).map(role => role.roleName).filter(Boolean);
  }

  editLink(customer: Customer): string[] {
    return this.isProfile() ? ['/profile/edit'] : ['/company/employees', String(customer.id), 'edit'];
  }

  backLink(): string[] {
    return this.isProfile() ? ['/loads'] : ['/company'];
  }

  formatBoolean(value: boolean | undefined): string {
    return value ? 'Yes' : 'No';
  }
}
